import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router';

import { Alert } from 'components';
import {
  ClientGroupSource,
  ClientGroupSourceUpdate,
  ContentApproval,
  GroupSourceType,
  SourceStatus,
  UpdateGroupSourceMutation,
  useDeleteGroupSourceMutation,
  useUpdateGroupSourceMutation,
} from 'api';
import { groupSource } from 'apollo/reducers';
import { clientGroupSourceDeleteResponse, clientGroupSourceUpdateResponse } from 'apollo/responses';
import { useGroupData, useNotify, usePrevious } from 'hooks';

import { IGroupForm } from '../../types';
import { getGroupHexColor } from '../../helpers';

interface IProps {
  source: ClientGroupSource;
}

interface IGroupParams {
  group_id: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
  },
  deleteIcon: {
    transform: 'rotate(45deg)',
    color: theme.palette.grey[500],
  },
  checkboxColor: {
    color: theme.palette.grey[700],
    '&$checked': {
      color: theme.palette.grey[700],
    },
  },
  checked: {},
}));

const Source: FC<IProps> = ({ source }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const {
    values: { title, color },
  } = useFormikContext<IGroupForm>();
  const { group_id } = useParams<IGroupParams>();
  const { groupColors } = useGroupData();
  const hexColor = getGroupHexColor(groupColors, color);
  const classes = useStyles({ hexColor });

  const { id, source_status, source_value, source_type, approval } = source;

  const prevApproval = usePrevious(approval);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [deleteGroupSourceMutation] = useDeleteGroupSourceMutation({
    onError: notify.mutationError,
    onCompleted: onCompletedDelete,
  });

  const [updateGroupSourceMutation] = useUpdateGroupSourceMutation({
    onError: notify.mutationError,
    onCompleted: onCompletedUpdate,
  });

  function toggleConfirm() {
    setDeleteOpen(!deleteOpen);
  }

  function handleDelete() {
    toggleConfirm();

    return deleteGroupSourceMutation({
      variables: { id },
      optimisticResponse: clientGroupSourceDeleteResponse(),
      update: (cache, result) =>
        groupSource({
          name: 'deleteGroupSource',
          cache,
          result,
          variables: { id, group_id },
        }),
    });
  }

  function handleUpdate(updateSource: ClientGroupSourceUpdate) {
    return updateGroupSourceMutation({
      variables: { data: updateSource },
      optimisticResponse: clientGroupSourceUpdateResponse(updateSource),
      update: (cache, result) =>
        groupSource({
          name: 'updateGroupSource',
          cache,
          result,
          variables: { group_id },
        }),
    });
  }

  function onCompletedDelete() {
    const message = t('groups:deleteSourceSuccess', {
      value: source_value,
      group: title,
    });
    return notify.info({ message });
  }

  function onCompletedUpdate(data: UpdateGroupSourceMutation) {
    let message = '';
    if (data.clientGroupSourceUpdate.source_status !== source_status) {
      message = t('groups:sourceStatusUpdate', {
        value: source_value,
        status: data.clientGroupSourceUpdate.source_status,
      });
    }

    if (data.clientGroupSourceUpdate.approval !== prevApproval) {
      message = t('groups:autoApproveUpdate', {
        value: source_value,
        status:
          data.clientGroupSourceUpdate.approval === ContentApproval.Autoapproved
            ? t('common:enabled')
            : t('common:disabled'),
      });
    }
    return notify.info({ message });
  }

  function onChangeSwitch() {
    const status =
      source_status === SourceStatus.Disabled ? SourceStatus.Active : SourceStatus.Disabled;
    return handleUpdate({
      id,
      source_status: status,
    });
  }

  function onChangeCheckbox() {
    const approvalOption =
      approval === ContentApproval.Autoapproved
        ? ContentApproval.Submitted
        : ContentApproval.Autoapproved;
    return handleUpdate({
      id,
      approval: approvalOption,
    });
  }

  function renderValue() {
    if (source_type === GroupSourceType.Keyword) {
      return (
        <Typography variant="subtitle2">{`${t('groups:keyword')}: ${source_value}`}</Typography>
      );
    }

    return <Typography variant="subtitle2">{source_value}</Typography>;
  }

  return (
    <Grid container alignItems="center">
      <Grid
        item
        xs
        container
        className={classes.container}
        alignItems="center"
        justify="space-between">
        <Grid item>{renderValue()}</Grid>
        <Switch
          color="default"
          checked={source_status === SourceStatus.Active}
          onChange={onChangeSwitch}
        />

        {source_status === SourceStatus.Active && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={approval === ContentApproval.Autoapproved}
                  onChange={onChangeCheckbox}
                  classes={{ root: classes.checkboxColor, checked: classes.checked }}
                />
              }
              label={t('groups:autoApprove')}
            />
          </Grid>
        )}
      </Grid>
      <IconButton onClick={() => setDeleteOpen(true)}>
        <AddCircleIcon className={classes.deleteIcon} />
      </IconButton>

      <Alert
        open={deleteOpen}
        onConfirm={handleDelete}
        onCancel={toggleConfirm}
        message={t('groups:deleteSourceAlert')}
        confirm={t('common:delete')}
      />
    </Grid>
  );
};

export default Source;
